import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Divider, FormControlLabel, Grid, InputLabel, MenuItem, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";

/**
 * Regras do cliente. Migração para o Jurídico: no cliente marcado, a ficha ganha outro Código Cliente quando vai para
 * a carteira jurídica (o anterior fica guardado no devedor). Prestação de Contas: o modelo (padrão ou UMC, com o
 * arquivo de retorno) e, no da UMC, a geração sozinha todo dia útil. Grava ao mudar.
 */
export default function RegrasCliente({ pessoaId }) {
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [config, setConfig] = useState(null);

    useEffect(() => {
        if (!pessoaId) return;
        setCarregando(true);
        get(`/cliente-regras/${pessoaId}`)
            .then(res => setConfig(res || {}))
            .catch(err => errorAlert(err?.message || "Erro ao carregar as regras do cliente."))
            .finally(() => setCarregando(false));
    }, [pessoaId]);

    const salvar = (alteracao, mensagem) => {
        setSalvando(true);
        post("/cliente-regras/save", { idPessoa: pessoaId, ...alteracao })
            .then(res => {
                setConfig(res || {});
                successAlert(mensagem);
            })
            .catch(err => errorAlert(err?.message || "Erro ao salvar as regras do cliente."))
            .finally(() => setSalvando(false));
    };

    const podeAlterar = !!config?.podeAlterar;
    const modeloUmc = config?.modeloPrestacaoContas === "UMC";

    if (carregando) {
        return (
            <MDBox>
                <Divider sx={{ my: 0 }} />
                <MDBox display="flex" justifyContent="center" py={3}><CircularProgress size={28} color="info" /></MDBox>
            </MDBox>
        );
    }

    return (
        <MDBox>
            <Divider sx={{ my: 0 }} />
            <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium">Migração para o Jurídico</MDTypography>
                <FormControlLabel
                    sx={{ ml: 0, mt: 1 }}
                    control={
                        <Switch
                            checked={!!config?.codigoNovoJuridico}
                            disabled={!podeAlterar || salvando}
                            onChange={(e) => salvar({ codigoNovoJuridico: e.target.checked },
                                e.target.checked ? "Código novo na migração para o Jurídico: ligado." : "Código novo na migração para o Jurídico: desligado.")}
                        />
                    }
                    label={
                        <MDTypography variant="button" fontWeight="regular" color="text" ml={1}>
                            O Código Cliente muda quando a ficha vai para a carteira jurídica
                        </MDTypography>
                    }
                />
                <MDTypography variant="caption" color="text" display="block" px={1}>
                    Ligado, a migração para a carteira jurídica (direta ou pela Fila de Solicitações) aceita o código novo.
                    O código anterior fica guardado no devedor e a busca da Base de Dados e da Baixa de Pagamento continua
                    achando a ficha por ele; a importação de remessa usa só o código atual.
                    {!podeAlterar && " Para alterar, o seu perfil precisa da opção Editar em Clientes."}
                </MDTypography>
            </MDBox>
            <Divider sx={{ my: 0 }} />
            <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium">Prestação de Contas</MDTypography>
                <Grid container spacing={2} mt={0} alignItems="flex-start">
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Modelo</InputLabel>
                        <MDInput
                            select
                            size="small"
                            fullWidth
                            value={config?.modeloPrestacaoContas || ""}
                            disabled={!podeAlterar || salvando}
                            onChange={(e) => salvar({ modeloPrestacaoContas: e.target.value },
                                e.target.value === "UMC" ? "Prestação de Contas no modelo da UMC." : "Prestação de Contas no modelo padrão.")}
                        >
                            <MenuItem value="">Padrão (planilha de pagamentos do período)</MenuItem>
                            <MenuItem value="UMC">UMC (planilha e arquivo de retorno por dia de pagamento)</MenuItem>
                        </MDInput>
                    </Grid>
                    {modeloUmc && (
                        <Grid item xs={12} sm={8}>
                            <FormControlLabel
                                sx={{ ml: 0, mt: 2.5 }}
                                control={
                                    <Switch
                                        checked={!!config?.pcAutomatica}
                                        disabled={!podeAlterar || salvando}
                                        onChange={(e) => salvar({ pcAutomatica: e.target.checked },
                                            e.target.checked ? "Geração automática da Prestação de Contas: ligada." : "Geração automática da Prestação de Contas: desligada.")}
                                    />
                                }
                                label={
                                    <MDTypography variant="button" fontWeight="regular" color="text" ml={1}>
                                        Gerar sozinha todo dia útil
                                    </MDTypography>
                                }
                            />
                        </Grid>
                    )}
                </Grid>
                <MDTypography variant="caption" color="text" display="block" px={1} mt={1}>
                    {modeloUmc
                        ? "Por data de pagamento e carteira: a planilha da PC (acordos, títulos negociados e liquidações) e o arquivo de retorno da UMC, salvos na pasta da Prestação de Contas. Ligada, a geração automática roda às 7h dos dias úteis com os pagamentos dos dias anteriores ainda sem PC."
                        : "A planilha da Prestação de Contas com os pagamentos do período (Relatórios > Prestação de Contas)."}
                    {!podeAlterar && " Para alterar, o seu perfil precisa da opção Editar em Clientes."}
                </MDTypography>
            </MDBox>
        </MDBox>
    );
}

RegrasCliente.propTypes = {
    pessoaId: PropTypes.string.isRequired,
};
